import React, { Context, createContext, PropsWithChildren, useContext, useState, useEffect, useCallback } from 'react'
import { callApi, TApiResponse } from '../api'
import { IIntegration, IntegrationTypes, TIntegrationActions } from '../types'
import { Modal, notification } from 'antd'
import { useHistory } from 'react-router-dom'
import QueryString from 'query-string'
import { customLocalStorage } from '../customStorage'
import { fetchInvite } from '../admin/utils'
import { RoutePath } from '../../components/routes'

/* eslint-disable @typescript-eslint/ban-types */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-empty-function */

const initialIntegrationState: IIntegration = {
	config: null,
	content: null,
	onboarding: null,
	phone_rules: null,
	loading: false,
	referrer: '',
	name: '',
	isInviteExpert: false,
}

const IntegrationContext: Context<{
	integration: IIntegration
	actions: TIntegrationActions
}> = createContext({
	integration: {
		...initialIntegrationState,
	},
	actions: {
		getConfig: () => Promise.resolve(),
		clearData: () => {},
		setQueryReferrer: (queryReferrer: string) => {},
	},
})

export const IntegrationProvider = (props: PropsWithChildren<{}>) => {
	const [config, setConfig] = useState<any>()
	const [content, setContent] = useState<any>(initialIntegrationState.content)
	const [name, setName] = useState<string>(initialIntegrationState.name)
	const [onboarding, setOnboarding] = useState<any>(initialIntegrationState.onboarding)
	const [phoneRules, setPhoneRules] = useState<any>(initialIntegrationState.phone_rules)
	const [loading, setLoading] = useState<boolean>(true)
	const [isInviteExpert, setIsInviteExpert] = useState<boolean>(false)
	const [referrer, setReferrer] = useState<string>()
	const [type, setType] = useState<IntegrationTypes>()
	const history = useHistory()

	const query: { [key: string]: any } = QueryString.parse(history.location.search, {
		arrayFormat: 'comma',
		parseNumbers: true,
	})

	useEffect(() => {
		const { referrer: queryReferrer, invitecode } = query
		const hasReferrer = Object.keys(query).includes('referrer')
		const hasInviteCode = Object.keys(query).includes('invitecode')
		const isReferrerEmpty = hasReferrer && !queryReferrer
		const isInviteCodeEmpty = hasInviteCode && !invitecode

		setQueryReferrer(queryReferrer)
		if (isReferrerEmpty && isInviteCodeEmpty) {
			history.replace('/no-configuration')
			setLoading(false)
		} else {
			const integrationType =
				window.location === window.parent.location ? IntegrationTypes.top_level : IntegrationTypes.embed
			setType(integrationType)
			if (queryReferrer) {
				setReferrer(queryReferrer)
			} else if (invitecode) {
				setLoading(true)
				fetchInvite(invitecode as string)
					.then((invite) => {
						setReferrer(invite.referrer_id)
						setIsInviteExpert(invite.is_expert)
					})
					.catch(({ message }) => {
						notification.error({ message })
						setLoading(false)
						history.push(RoutePath.Register)
					})
					.finally(() => {})
			} else {
				setLoading(false)
			}
		}
		const historySearch = QueryString.stringify(
			{
				...query,
				referrer: undefined,
			},
			{ arrayFormat: 'comma' }
		)
		history.replace({ search: historySearch })
	}, [])

	useEffect(() => {
		if (referrer && type) {
			getConfig()
		}
	}, [referrer, type])

	useEffect(() => {
		if (config && config.intro_modal) {
			Modal.info({
				content: <div dangerouslySetInnerHTML={{ __html: config.intro_modal }} />,
				okButtonProps: {
					shape: 'round',
				},
				cancelButtonProps: {
					shape: 'round',
				},
				okText: "Let's Go!",
				maskClosable: true,
				centered: true,
				icon: false,
			})
		}
	}, [config])

	const fetchIntegrationConfig = async () => {
		setLoading(true)

		const cronofyResponse: TApiResponse = await callApi(`channel/config/${referrer}/${type}`, {})
		const { data } = cronofyResponse.payload

		if (data.onboarding) {
			setOnboarding(data.onboarding)
		}

		if (data.phone_rules) {
			setPhoneRules(data.phone_rules)
		}

		if (cronofyResponse.ok && data.config) {
			if (!data.config) {
				history.replace('/no-configuration')
			}

			setConfig(data.config)
			setName(data.name)
		}

		if (data.content) {
			setContent(data.content)
		}

		setLoading(false)
	}

	const getConfig = useCallback(fetchIntegrationConfig, [referrer, type])
	const clearData = () => {
		setConfig(null)
		setContent(initialIntegrationState.content)
		setName(initialIntegrationState.name)
		setOnboarding(initialIntegrationState.onboarding)
		setPhoneRules(initialIntegrationState.phone_rules)
		setIsInviteExpert(false)
		setType(null)
	}

	const setQueryReferrer = useCallback((queryReferrer?: string) => {
		if (queryReferrer) {
			customLocalStorage.setItem('referrer', queryReferrer)
			customLocalStorage.removeItem('wiseInviteCode')
		}
	}, [])

	const actions = {
		getConfig,
		setQueryReferrer,
		clearData,
	}

	return (
		<IntegrationContext.Provider
			value={{
				integration: {
					loading,
					config,
					type,
					referrer,
					content,
					name,
					onboarding,
					isInviteExpert,
					phone_rules: phoneRules,
				},
				actions,
			}}
		>
			{props.children}
		</IntegrationContext.Provider>
	)
}

export const useIntegrationContext: () => { integration: IIntegration; actions: TIntegrationActions } = () => {
	return useContext(IntegrationContext)
}
